import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { loadGroupData, BarCompareConstNat, WardGroupStaticMapWithBase, ConstituencyGroupStaticMap, fetchWardGroup, fetchConstituencyGroup } from "./utils/metricsClient";
import { getPolConfig } from "./utils/polAggregators";
import { geoAssetUrl } from "./utils/geoAssets";
import { computeTopAudiences, weightedNationalByKey } from "./utils/computeTopAudiences";

/* ─── Design tokens (aligned with ConstituencyReportView) ──────────────── */
const INK    = "#0f172a";
const INK2   = "#64748b";
const INK3   = "#94a3b8";
const BORDER = "#e2e8f0";
const TEAL   = "#0abfbc";
const DARK   = "#2c2c2a";
const BLUE   = "#185fa5";

const DEFAULT_TOP_N = 3;

/* Section label */
const SL = ({ children, color = TEAL }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "5px", flexShrink: 0 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: DARK, whiteSpace: "nowrap" }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);

const isNoiseKey = (o) =>
  /dont.?know|dk|none|refus|not\s*sure|other/i.test(`${o.key} ${o.label}`);

// Positive-response level tokens across likert-grid question families.
const POS_LEVELS = new Set([
  "trust_completely", "tend_trust",            // sector trust
  "comfortable", "very_comfortable",           // windfall comfort
  "strongly_agree", "agree", "tend_agree",     // agreement grids
  "support", "strongly_support", "approve",
]);

// Extract the likert level token from a grid key for a given sub-variable, e.g.
// ("ind:trust_farming:tend_trust:cat", "trust_farming") -> "tend_trust".
const levelForSub = (key, sub) => {
  const marker = `:${sub}:`;
  const i = key.indexOf(marker);
  if (i < 0) return null;
  return key.slice(i + marker.length).split(":")[0];
};

// Diverging likert palette derived from a group's polarity (pos/neu/neg/dk).
// Individual likert questions don't ship a `colors` map, so without this every
// response renders grey.
const LIKERT_POS = ["#1b5e20", "#66bb6a", "#a5d6a7"]; // strong → mild trust (green)
const LIKERT_NEG = ["#ef9a9a", "#c62828", "#7f1d1d"]; // mild → strong distrust (red)
function likertColors(group) {
  const pol = group.polarity || {};
  const asArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  const out = {};
  asArr(pol.pos).forEach((k, i) => { out[k] = LIKERT_POS[Math.min(i, LIKERT_POS.length - 1)]; });
  asArr(pol.neg).forEach((k, i) => { out[k] = LIKERT_NEG[Math.min(i, LIKERT_NEG.length - 1)]; });
  asArr(pol.neu).forEach((k) => { out[k] = "#9e9e9e"; });
  asArr(pol.dk).forEach((k) => { out[k] = "#cfd5dd"; });
  return out;
}

// Resolve a { key: color } map for a group, falling back sensibly when the
// group has no explicit colours (likert → diverging scale; else a palette).
const PALETTE = ["#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f", "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ab"];
function resolveGroupColors(group) {
  if (!group) return {};
  const base = group.colors && Object.keys(group.colors).length ? { ...group.colors } : {};
  if (group.type === "likert" && group.polarity) {
    return { ...likertColors(group), ...base };
  }
  if (!Object.keys(base).length) {
    (group.keys || []).forEach((k, i) => { base[k] = PALETTE[i % PALETTE.length]; });
  }
  return base;
}

// Normalise a group + loadGroupData rows into a flat list of "options" to chart
// and profile. For a likert_grid this collapses the sector × level matrix into
// one option per sector, valued by its positive-response share (sum of positive
// level columns). For everything else each response key is its own option.
function buildOptions(group, raw) {
  const rows = raw || [];
  if (group?.type === "likert_grid" && Array.isArray(group.subVariables)) {
    const byKey = {};
    rows.forEach((r) => { byKey[r.key] = r; });
    const sumFor = (keys, field) =>
      keys.reduce((acc, k) => {
        const v = Number(byKey[k]?.[field]);
        return Number.isFinite(v) ? acc + v : acc;
      }, 0);
    let allGroups = {};
    try { allGroups = getPolConfig().groupsById || {}; } catch { allGroups = {}; }
    return group.subVariables
      .map((sub) => {
        // Prefer the sub-question's own declared positive polarity - its keys
        // match the grid's and this works regardless of key naming scheme
        // (sector grids use `ind:sub:level:cat`, motability uses `sub_level`).
        let posKeys = [];
        const pol = allGroups[sub]?.polarity?.pos;
        if (pol) posKeys = Array.isArray(pol) ? pol : [pol];
        // Fallbacks: colon/underscore level parsing, else all of the sub's keys.
        if (!posKeys.length) {
          const matched = (group.keys || []).filter(
            (k) => k.indexOf(`:${sub}:`) >= 0 || k.startsWith(`${sub}_`)
          );
          posKeys = matched.filter((k) => POS_LEVELS.has(levelForSub(k, sub)));
          if (!posKeys.length) posKeys = matched;
        }
        return {
          key: sub,
          label: group.subLabels?.[sub] || sub,
          color: group.subColors?.[sub] || "#64748b",
          constituency: sumFor(posKeys, "constituency"),
          national: sumFor(posKeys, "national"),
          sumKeys: posKeys,
        };
      })
      .filter((o) => Number.isFinite(Number(o.constituency)));
  }
  // Flat categorical / likert: each response key is an option.
  const colorByKey = resolveGroupColors(group);
  return rows.map((o) => ({ ...o, color: colorByKey[o.key] || o.color, sumKeys: [o.key] }));
}

/* Small horizontal bar for a demographic segment score */
const SegBar = ({ label, score, max, color = TEAL }) => {
  const pct = max > 0 ? Math.max(4, (score / max) * 100) : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "3px" }}>
      <Typography sx={{ fontSize: 8, color: INK2, width: 96, flexShrink: 0, lineHeight: 1.2, textAlign: "right" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 9, bgcolor: "#f1f5f9", borderRadius: "5px", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, width: `${pct}%`, bgcolor: color, borderRadius: "5px" }} />
      </Box>
      <Typography sx={{ fontSize: 8, fontWeight: 700, color: INK, width: 30, flexShrink: 0 }}>
        {Number.isFinite(score) ? `${score.toFixed(0)}%` : "-"}
      </Typography>
    </Box>
  );
};

/* Audience card for one option */
const AudienceCard = ({ option, audience, loading, accent }) => {
  const perDim = audience?.perDimension || {};
  const dims = Object.values(perDim).sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const max = dims.length ? Math.max(...dims.map((d) => d.score || 0)) : 0;
  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderTop: `3px solid ${accent}`,
        borderRadius: "9px",
        p: "9px 10px",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "5px" }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 750, color: INK, lineHeight: 1.15, pr: "6px" }}>
          {option.label}
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: accent, flexShrink: 0 }}>
          {Number.isFinite(Number(option.constituency)) ? `${Number(option.constituency).toFixed(0)}%` : "-"}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", py: "8px" }}>
          <CircularProgress size={12} sx={{ color: accent }} />
          <Typography sx={{ fontSize: 8.5, color: INK3 }}>Computing top audiences…</Typography>
        </Box>
      ) : !audience ? (
        <Typography sx={{ fontSize: 8.5, color: INK3, py: "6px" }}>
          No demographic breakdown available for this option.
        </Typography>
      ) : (
        <>
          {audience.combined && (
            <Box sx={{ mb: "6px", pb: "5px", borderBottom: `1px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: accent }}>
                Strongest audience
              </Typography>
              <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
                {audience.combined.label}
                <Box component="span" sx={{ fontSize: 8.5, fontWeight: 600, color: INK2 }}>
                  {" "}· {audience.combined.score.toFixed(0)}%
                </Box>
              </Typography>
            </Box>
          )}
          {dims.map((d) => (
            <SegBar key={d.dimension} label={`${d.dimension}: ${d.label}`} score={d.score} max={max} color={accent} />
          ))}
        </>
      )}
    </Box>
  );
};

const QuestionDrilldownView = ({
  areaCode,
  constName,
  groupId,
  optionKeys = null,   // null => auto (top options); array => profile these keys
  topN = DEFAULT_TOP_N,
  height = "1123px",
  national = false,    // true => whole-GB report (constituency map, no ward table)
}) => {
  const group = useMemo(() => {
    try { return getPolConfig().groupsById?.[groupId] || null; } catch { return null; }
  }, [groupId]);

  const [optionData, setOptionData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [audiences, setAudiences] = useState({});        // optionKey -> { perDimension, topPairs, combined }
  const [audiencesLoading, setAudiencesLoading] = useState(true);
  const [wardData, setWardData] = useState({ wards: [], loading: true });
  const reqKey = useRef(null);

  // Load option-level data. National scope uses the national values (mapped into
  // the `constituency` slot so the same option model / chart apply).
  useEffect(() => {
    if (!groupId || (!national && !areaCode)) return;
    let cancelled = false;
    setLoadingData(true);

    const load = national
      // National overall = n_raked-weighted mean across constituencies at the
      // latest wave, matching computeTopAudiences' basis exactly (so a top
      // audience is never below its option's overall %). Note: the standalone
      // national dataset isn't available locally and averages unweighted, so we
      // aggregate the constituency dataset on the client instead.
      ? fetchConstituencyGroup(groupId).then(({ rows, keys }) => {
          const weighted = weightedNationalByKey(rows, keys);
          const labels = group?.labels || {};
          const colors = group?.colors || {};
          return (keys || []).map((k) => ({
            key: k,
            label: labels[k] || k,
            constituency: weighted[k],
            national: null,
            color: colors[k] || "#888",
          }));
        })
      : loadGroupData({ groupId, areaCode }).then((d) => d || []);

    load
      .then((rows) => {
        if (cancelled) return;
        setOptionData(buildOptions(group, rows));
        setLoadingData(false);
      })
      .catch(() => { if (!cancelled) { setOptionData([]); setLoadingData(false); } });
    return () => { cancelled = true; };
  }, [areaCode, groupId, group, national]);

  // Determine which options to profile for audiences
  const profiledOptions = useMemo(() => {
    if (!optionData.length) return [];
    if (optionKeys && optionKeys.length) {
      const set = new Set(optionKeys);
      return optionData.filter((o) => set.has(o.key));
    }
    return [...optionData]
      .filter((o) => Number.isFinite(Number(o.constituency)) && !isNoiseKey(o))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))
      .slice(0, topN);
  }, [optionData, optionKeys, topN]);

  // Compute audiences for all profiled options in one batched scan. Requests
  // the full option set with the group's normalise flag so values are on the
  // same scale as the chart (see computeTopAudiences).
  useEffect(() => {
    if (!profiledOptions.length || !group) return;
    const theme = group.theme || (String(group.keys?.[0] || "").startsWith("ind:") ? "ind" : "pol");
    const schema = group.schema || "tracking";
    const profileKeys = profiledOptions.map((o) => o.key);
    const key = `${national ? "GB" : areaCode}|${groupId}|${profileKeys.join(",")}`;
    if (reqKey.current === key) return;
    reqKey.current = key;

    const controller = new AbortController();
    let cancelled = false;
    setAudiences({});
    setAudiencesLoading(true);

    (async () => {
      let result = {};
      try {
        result = await computeTopAudiences({
          areaCode,
          national,
          groupKeys: group.keys || [],
          options: profiledOptions.map((o) => ({ key: o.key, sumKeys: o.sumKeys || [o.key] })),
          theme,
          schema,
          level: "constituency",
          normaliseOverOptions: !!group.normalise_over_options,
          waves: group.waves || null,
          signal: controller.signal,
        });
      } catch { result = {}; }
      if (cancelled) return;
      setAudiences(result || {});
      setAudiencesLoading(false);
    })();

    return () => { cancelled = true; controller.abort(); };
  }, [profiledOptions, group, areaCode, groupId, national]);

  // Per-area rows for the results table: wards (of the constituency) for a
  // constituency report, or all GB constituencies for a national report.
  useEffect(() => {
    if (!groupId || !optionData.length) return;
    if (!national && !areaCode) return;
    let cancelled = false;
    setWardData({ wards: [], loading: true });
    (async () => {
      try {
        if (national) {
          const [{ rows }, fc] = await Promise.all([
            fetchConstituencyGroup(groupId),
            fetch(geoAssetUrl("/ukmaps/2024_constituency.json")).then((r) => r.json()),
          ]);
          const nameByCode = {};
          (fc.features || []).forEach((f) => {
            nameByCode[String(f.properties?.PCON24CD)] = f.properties?.PCON24NM;
          });
          const list = (rows || []).map((r) => {
            const code = String(r.area_cd || r.PCON24CD || r.area_id);
            return { code, name: nameByCode[code] || code, row: r };
          });
          if (!cancelled) setWardData({ wards: list, loading: false });
          return;
        }

        const [{ rows }, fc] = await Promise.all([
          fetchWardGroup(areaCode, groupId),
          fetch(geoAssetUrl("/ukmaps/2024_ward_withPCON.json")).then((r) => r.json()),
        ]);
        const nameByCode = {};
        (fc.features || []).forEach((f) => {
          if (String(f.properties?.PCON24CD) === String(areaCode)) {
            nameByCode[String(f.properties?.WD24CD)] = f.properties?.WD24NM;
          }
        });
        // Latest wave per ward - keep the full row so we can read any response.
        const byWard = new Map();
        (rows || []).forEach((r) => {
          const code = String(r.WD24CD || r.area_cd || r.area_id);
          const w = Number(r.Wave) || 0;
          const prev = byWard.get(code);
          if (!prev || w >= prev._w) byWard.set(code, { ...r, _w: w });
        });
        const wards = [];
        byWard.forEach((row, code) => {
          wards.push({ code, name: nameByCode[code] || code, row });
        });
        if (!cancelled) setWardData({ wards, loading: false });
      } catch {
        if (!cancelled) setWardData({ wards: [], loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [areaCode, groupId, optionData, national]);

  /* ── Derived stats ── */
  const ranked = useMemo(() =>
    [...optionData]
      .filter((o) => Number.isFinite(Number(o.constituency)) && !isNoiseKey(o))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency)),
  [optionData]);
  const leader = ranked[0] || null;
  const biggestGap = useMemo(() => {
    const withGap = optionData
      .filter((o) => Number.isFinite(Number(o.constituency)) && Number.isFinite(Number(o.national)) && !isNoiseKey(o))
      .map((o) => ({ ...o, gap: Number(o.constituency) - Number(o.national) }));
    return withGap.sort((a, b) => b.gap - a.gap)[0] || null;
  }, [optionData]);

  const fulltext = group?.fulltext || group?.label || "";
  const accents = [TEAL, BLUE, "#5b21b6", "#b45309"];
  // Colour override for the ward map - derived diverging colours for individual
  // likert questions (which ship no `colors`). Grids keep their default map.
  const mapColors = useMemo(
    () => (!group || group.type === "likert_grid" ? undefined : resolveGroupColors(group)),
    [group]
  );

  return (
    <Box
      id="report-content"
      sx={{
        backgroundColor: "white",
        color: "black",
        width: "794px",
        height,
        overflow: "hidden",
        mx: "auto",
        p: "14px 18px",
        border: "1px solid #ccc",
        boxSizing: "border-box",
        display: "grid",
        gridTemplateRows: "auto auto 1.15fr 1.45fr auto auto",
        gridTemplateColumns: "1fr",
        gap: "9px",
      }}
    >
      {/* Header */}
      <Box sx={{ pb: "6px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "3px" }}>
          <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
          <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
            Question Drill-down
          </Typography>
          <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {constName}
        </Typography>
        {fulltext && (
          <Typography sx={{ fontSize: 10.5, color: INK2, mt: "2px", lineHeight: 1.3 }}>
            {fulltext}
          </Typography>
        )}
      </Box>

      {/* Stat strip (the "vs national" gap is dropped in national reports) */}
      <Box sx={{ display: "grid", gridTemplateColumns: national ? "1fr" : "1fr 1fr", gap: "8px" }}>
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: "7px 10px", bgcolor: "#f8fafc" }}>
          <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL }}>
            {national ? "Leading response nationally" : "Leading response"}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 750, color: INK, lineHeight: 1.2 }}>
            {leader ? `${leader.label} · ${Number(leader.constituency).toFixed(1)}%` : "-"}
          </Typography>
        </Box>
        {!national && (
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: "7px 10px", bgcolor: "#f8fafc" }}>
            <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: BLUE }}>
              Biggest gap vs national
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 750, color: INK, lineHeight: 1.2 }}>
              {biggestGap
                ? `${biggestGap.label} · ${biggestGap.gap > 0 ? "+" : ""}${biggestGap.gap.toFixed(1)}pp`
                : "-"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Map - leading response by ward (constituency) or by constituency (national) */}
      <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
        {national ? (
          <ConstituencyGroupStaticMap
            groupId={groupId}
            colors={mapColors}
            options={group?.type === "likert_grid" ? optionData : undefined}
          />
        ) : (
          <WardGroupStaticMapWithBase
            groupId={groupId}
            areaCode={areaCode}
            colors={mapColors}
            options={group?.type === "likert_grid" ? optionData : undefined}
          />
        )}
        <Box sx={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
          borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
        }}>
          <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>
            {national ? "Leading response by constituency" : "Leading response by ward"}
          </Typography>
        </Box>
      </Box>

      {/* Breakdown (left) + results-by-area table (right) */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", minHeight: 0 }}>
        {/* Left: response breakdown bar chart */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <SL color={BLUE}>{national ? "National response breakdown" : "Response breakdown - local vs national"}</SL>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {loadingData ? (
              <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                <CircularProgress size={20} sx={{ color: TEAL }} />
              </Box>
            ) : (
              <BarCompareConstNat data={optionData} constName={constName} hideNational={national} />
            )}
          </Box>
        </Box>

        {/* Right: the 3 profiled responses' share by ward (constituency) or by
            constituency (national — top 15 by the first/leading response). */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <SL color={TEAL}>{national ? "Top constituencies" : "Results by ward"}</SL>
          {(() => {
            const cols = profiledOptions.slice(0, 3);
            const valFor = (row, o) => {
              if (!row || !o) return 0;
              return (o.sumKeys || [o.key]).reduce((a, k) => {
                const x = Number(row[k]);
                return Number.isFinite(x) ? a + x : a;
              }, 0);
            };
            const sorted = wardData.wards
              .filter((w) => w && w.row)
              .sort((a, b) => (cols[0] ? valFor(b.row, cols[0]) - valFor(a.row, cols[0]) : 0));
            const wards = national ? sorted.slice(0, 15) : sorted;

            if (wardData.loading) {
              return (
                <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={16} sx={{ color: TEAL }} />
                </Box>
              );
            }
            if (!wards.length || !cols.length) {
              return (
                <Typography sx={{ fontSize: 8.5, color: INK3, mt: "4px" }}>
                  {national ? "No constituency data for this question." : "No ward-level data for this question."}
                </Typography>
              );
            }
            return (
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Column header */}
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4px", pb: "3px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }} />
                  {cols.map((o) => (
                    <Box key={o.key} sx={{ width: 52, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: o.color }} />
                      <Typography sx={{ fontSize: 7, fontWeight: 600, color: INK2, lineHeight: 1.05, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {o.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {/* Ward rows - flex to fill the available height */}
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  {wards.map((w, i) => (
                    <Box
                      key={w.code}
                      sx={{
                        flex: 1, minHeight: 0,
                        display: "flex", alignItems: "center", gap: "4px",
                        borderBottom: i < wards.length - 1 ? `0.5px solid ${BORDER}` : "none",
                      }}
                    >
                      <Typography sx={{ flex: 1, minWidth: 0, fontSize: 8.5, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {w.name}
                      </Typography>
                      {cols.map((o) => (
                        <Typography key={o.key} sx={{ width: 52, flexShrink: 0, fontSize: 8.5, fontWeight: 700, color: o.color, textAlign: "center" }}>
                          {valFor(w.row, o).toFixed(0)}%
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>

      {/* Audiences label */}
      <Box>
        <SL>Who backs each response - top audiences</SL>
        <Typography sx={{ fontSize: 8.5, color: INK2, lineHeight: 1.3 }}>
          Demographic sub-groups with the strongest support for each leading response {national ? "across Great Britain" : "in this constituency"}.
        </Typography>
      </Box>

      {/* Audience cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.max(1, Math.min(profiledOptions.length, 3))}, 1fr)`,
          gap: "9px",
          minHeight: 0,
        }}
      >
        {profiledOptions.slice(0, 3).map((o, i) => (
          <AudienceCard
            key={o.key}
            option={o}
            accent={accents[i % accents.length]}
            loading={audiencesLoading}
            audience={audiences[o.key] ?? null}
          />
        ))}
      </Box>
    </Box>
  );
};

export default QuestionDrilldownView;
